import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { updatePassword } from 'aws-amplify/auth';
import { Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/useToast';

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordChangeForm: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormData>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await updatePassword({
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      showSuccess('Contraseña actualizada exitosamente');
      reset();
    } catch (error: any) {
      const code = error?.name || error?.code || '';
      const message = error?.message || '';
      
      if (code === 'NotAuthorizedException') {
        showError({ message: 'La contraseña actual es incorrecta' }, 'Error al cambiar contraseña');
      } else if (code === 'InvalidPasswordException' || message.includes('Password did not conform')) {
        // Traducir mensajes de Cognito
        let friendlyMessage = 'La nueva contraseña no cumple con los requisitos de seguridad:';
        
        if (message.includes('numeric characters')) {
          friendlyMessage += '\n• Debe contener al menos un número';
        }
        if (message.includes('uppercase characters')) {
          friendlyMessage += '\n• Debe contener al menos una letra mayúscula';
        }
        if (message.includes('lowercase characters')) {
          friendlyMessage += '\n• Debe contener al menos una letra minúscula';
        }
        if (message.includes('symbol characters')) {
          friendlyMessage += '\n• Debe contener al menos un carácter especial (!@#$%^&*)';
        }
        if (message.includes('length')) {
          friendlyMessage += '\n• Debe tener al menos 8 caracteres';
        }
        
        // Si no se detectó ningún requisito específico, mostrar mensaje genérico
        if (friendlyMessage === 'La nueva contraseña no cumple con los requisitos de seguridad:') {
          friendlyMessage = 'La contraseña debe tener:\n• Al menos 8 caracteres\n• Al menos una letra mayúscula\n• Al menos una letra minúscula\n• Al menos un número\n• Al menos un carácter especial (!@#$%^&*)';
        }
        
        showError({ message: friendlyMessage }, 'Error al cambiar contraseña');
      } else {
        showError(error, 'Error al cambiar contraseña');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Current Password */}
      <div>
        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Contraseña actual <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showCurrentPassword ? "text" : "password"}
            id="currentPassword"
            {...register('currentPassword', {
              required: 'La contraseña actual es requerida',
            })}
            className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.currentPassword ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showCurrentPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>
        )}
      </div>

      {/* New Password */}
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Nueva contraseña <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? "text" : "password"}
            id="newPassword"
            {...register('newPassword', {
              required: 'La nueva contraseña es requerida',
              minLength: {
                value: 8,
                message: 'La contraseña debe tener al menos 8 caracteres',
              },
            })}
            className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.newPassword ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showNewPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.newPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
        )}
        <div className="mt-2 text-xs text-gray-500 space-y-1">
          <p className="font-medium">La contraseña debe contener:</p>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li>Al menos 8 caracteres</li>
            <li>Al menos una letra mayúscula (A-Z)</li>
            <li>Al menos una letra minúscula (a-z)</li>
            <li>Al menos un número (0-9)</li>
            <li>Al menos un carácter especial (!@#$%^&*)</li>
          </ul>
        </div>
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Confirmar nueva contraseña <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            {...register('confirmPassword', {
              required: 'Debe confirmar la nueva contraseña',
              validate: (value) =>
                value === newPassword || 'Las contraseñas no coinciden',
            })}
            className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
        )}
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Cambiando...
            </>
          ) : (
            'Cambiar contraseña'
          )}
        </button>
      </div>
    </form>
  );
};

export default PasswordChangeForm;
